import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// Modern kit playground slide on a 1x2 tile patch: a small platform ~1.2 m
// high on four galvanized legs, a short ladder (two stringers + three rungs)
// up the -Z side, and a muted-red chute with low side rails sloping down to
// +Z. Authored at true metric size, centred on the origin.
const RED = "#b05a4a";
const GALV = "#9aa2a6";

const PLATFORM_Y = 1.2; // top of the platform deck
const DECK_T = 0.06;

// Chute geometry: from the platform edge down to the ground at +Z.
const CHUTE_TOP = { y: PLATFORM_Y - 0.04, z: -0.25 };
const CHUTE_END = { y: 0.15, z: 0.9 };
const CHUTE_DY = CHUTE_TOP.y - CHUTE_END.y;
const CHUTE_DZ = CHUTE_END.z - CHUTE_TOP.z;
const CHUTE_LEN = Math.hypot(CHUTE_DY, CHUTE_DZ) + 0.1;
const CHUTE_ANGLE = -Math.atan2(CHUTE_DY, CHUTE_DZ); // tilt +Z end down
const CHUTE_MID: [number, number] = [
  (CHUTE_TOP.y + CHUTE_END.y) / 2,
  (CHUTE_TOP.z + CHUTE_END.z) / 2,
];
// Offset perpendicular to the chute surface (local +Y rotated by the tilt).
const RAIL_LIFT = 0.09;
const RAIL_DY = RAIL_LIFT * Math.cos(CHUTE_ANGLE);
const RAIL_DZ = RAIL_LIFT * Math.sin(CHUTE_ANGLE);

export const playgroundSlide = defineModel({
  id: "playground-slide",
  batchId: "modern",
  name: "Playground slide",
  tags: ["modern", "outdoor", "playground", "kids", "garden", "slide"],
  summary: "A kids playground slide with a ladder and a red chute.",
  description:
    "A playground slide of the Modern Living kit on a 1x2 tile patch: a " +
    "small deck about 1.2 m high on galvanized legs, a three-rung ladder up " +
    "the -Z side and a muted-red chute with low side rails running down to +Z.",
  tiles: { w: 1, h: 2 },
  footprint: 1.2,
  parts(): Part[] {
    const legs: Part[] = [];
    for (const x of [-0.3, 0.3]) {
      for (const z of [-0.8, -0.28]) {
        legs.push({
          geometry: new THREE.BoxGeometry(0.06, PLATFORM_Y - DECK_T, 0.06),
          color: GALV,
          position: [x, (PLATFORM_Y - DECK_T) / 2, z],
        });
      }
    }
    const rungs: Part[] = [0.35, 0.65, 0.95].map((y) => ({
      geometry: new THREE.CylinderGeometry(0.02, 0.02, 0.34, 6),
      color: GALV,
      position: [0, y, -0.92],
      rotation: [0, 0, Math.PI / 2] as const,
    }));
    return [
      // Platform deck.
      {
        geometry: new THREE.BoxGeometry(0.7, DECK_T, 0.62),
        color: RED,
        position: [0, PLATFORM_Y - DECK_T / 2, -0.54],
      },
      ...legs,
      // Ladder stringers on the -Z side.
      {
        geometry: new THREE.BoxGeometry(0.04, 1.18, 0.04),
        color: GALV,
        position: [-0.17, 0.59, -0.92],
      },
      {
        geometry: new THREE.BoxGeometry(0.04, 1.18, 0.04),
        color: GALV,
        position: [0.17, 0.59, -0.92],
      },
      ...rungs,
      // Chute bed sloping down to +Z.
      {
        geometry: new THREE.BoxGeometry(0.42, 0.05, CHUTE_LEN),
        color: RED,
        position: [0, CHUTE_MID[0], CHUTE_MID[1]],
        rotation: [CHUTE_ANGLE, 0, 0],
      },
      // Low side rails on the chute.
      {
        geometry: new THREE.BoxGeometry(0.04, 0.13, CHUTE_LEN),
        color: RED,
        position: [-0.21, CHUTE_MID[0] + RAIL_DY, CHUTE_MID[1] + RAIL_DZ],
        rotation: [CHUTE_ANGLE, 0, 0],
      },
      {
        geometry: new THREE.BoxGeometry(0.04, 0.13, CHUTE_LEN),
        color: RED,
        position: [0.21, CHUTE_MID[0] + RAIL_DY, CHUTE_MID[1] + RAIL_DZ],
        rotation: [CHUTE_ANGLE, 0, 0],
      },
    ];
  },
  animations: [],
});
